import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { api, ApiError, setRequestBot } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { toast } from '@/shared/model/toast'
import type { Bot, BotsResponse } from '@/shared/api/types'

/**
 * Выбранный бот компании.
 *
 * Выбор живёт в браузере, а не на сервере: два сотрудника могут
 * одновременно править разных ботов, и общий «текущий бот» на сервере
 * мешал бы обоим. Помним его по компании — при входе под клиентом
 * админом чужой бот не должен подставиться в запросы.
 */
const STORAGE_KEY = 'sellora.bot'

interface Stored {
  companyId: number
  botId: number
}

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<Stored>
    if (typeof value.companyId === 'number' && typeof value.botId === 'number' && value.botId > 0) {
      return { companyId: value.companyId, botId: value.botId }
    }
  } catch {
    /* битая запись — считаем, что выбран основной */
  }
  return null
}

interface BotState {
  /** Компания, для которой выбран бот. null — ещё не знаем. */
  companyId: number | null
  /** null — основной бот. */
  botId: number | null
  set: (companyId: number, botId: number | null) => void
}

const initial = readStored()
// Ставим заголовок до первого запроса: иначе анкета успела бы загрузиться
// от основного и мигнуть чужими данными.
setRequestBot(initial?.botId ?? null)

export const useBotStore = create<BotState>((set) => ({
  companyId: initial?.companyId ?? null,
  botId: initial?.botId ?? null,
  set: (companyId, botId) => {
    setRequestBot(botId)
    try {
      if (botId === null) localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, JSON.stringify({ companyId, botId }))
    } catch {
      /* приватный режим: выбор проживёт до перезагрузки */
    }
    set({ companyId, botId })
  },
}))

/** Данные, которые различаются у ботов: при смене бота их надо перечитать. */
const BOT_SCOPED = [queryKeys.profile, queryKeys.setup, queryKeys.setupChat, queryKeys.promptPreview]

/**
 * Сброс, а не удаление: у открытой формы на запросе висит наблюдатель, и
 * удалённый из кэша запрос оставляет её в вечной загрузке. resetQueries
 * возвращает данные в исходное состояние и сам перезапрашивает активные.
 */
async function dropBotScoped(client: QueryClient) {
  for (const key of BOT_SCOPED) await client.cancelQueries({ queryKey: key })
}

async function refetchBotScoped(client: QueryClient) {
  await Promise.all(BOT_SCOPED.map((key) => client.resetQueries({ queryKey: key })))
}

/** Переключить бота: заголовок, память браузера и сброс кэша его данных. */
export function useSelectBot() {
  const client = useQueryClient()
  return async (companyId: number, botId: number | null) => {
    const state = useBotStore.getState()
    if (state.companyId === companyId && state.botId === botId) return
    // Бот тот же (основной), сменилась только привязка к компании — данные
    // в кэше верные, перезапрашивать нечего.
    if (state.botId === botId) {
      useBotStore.getState().set(companyId, botId)
      return
    }
    // Сначала гасим запросы старого бота, потом меняем заголовок: ответ,
    // пришедший после смены, иначе лёг бы в кэш нового.
    await dropBotScoped(client)
    useBotStore.getState().set(companyId, botId)
    await refetchBotScoped(client)
    void client.invalidateQueries({ queryKey: queryKeys.bots })
  }
}

export const botsApi = {
  list: (signal?: AbortSignal) => api.get<BotsResponse>('/bots', signal),
  create: (name: string, copyFrom: number | null) => api.post<Bot>('/bots', { name, copyFrom }),
  rename: (id: number, name: string) => api.put<Bot>(`/bots/${id}`, { name }),
  remove: (id: number) => api.delete<void>(`/bots/${id}`),
  promote: (id: number) => api.post<{ backup: Bot }>(`/bots/${id}/promote`),
}

export function useBots() {
  return useQuery({
    queryKey: queryKeys.bots,
    queryFn: ({ signal }) => botsApi.list(signal),
  })
}

/**
 * Сверяет выбранного бота с компанией и списком.
 *
 * Сменилась компания (вход под клиентом, другой аккаунт) или бота удалили
 * в соседней вкладке — возвращаемся на основного, а не шлём запросы с
 * несуществующим ботом.
 */
export function useBotGuard(companyId: number | undefined) {
  const bots = useBots()
  const select = useSelectBot()
  const { companyId: storedCompany, botId } = useBotStore()

  useEffect(() => {
    if (companyId === undefined) return
    if (storedCompany !== companyId) {
      void select(companyId, null)
      return
    }
    // Только по свежему списку: сразу после создания бота старый список
    // его ещё не знает, и сброс на основного отменил бы только что
    // сделанный выбор.
    if (botId !== null && bots.data && !bots.isFetching && !bots.data.items.some((b) => b.id === botId)) {
      void select(companyId, null)
      toast.info('Выбранного бота больше нет — открыт основной')
    }
  }, [companyId, storedCompany, botId, bots.data, bots.isFetching, select])
}

/** Название текущего бота для плашек и подписей. */
export function useCurrentBot() {
  const bots = useBots()
  const botId = useBotStore((s) => s.botId)
  const bot = botId === null ? null : (bots.data?.items.find((b) => b.id === botId) ?? null)
  return {
    botId,
    bot,
    name: botId === null ? 'Основной бот' : (bot?.name ?? 'Бот'),
    isMain: botId === null,
  }
}

function onError(fallback: string) {
  return (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : fallback)
}

export function useCreateBot() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ name, copyFrom }: { name: string; copyFrom: number | null }) =>
      botsApi.create(name, copyFrom),
    onSuccess: (bot) => {
      // Кладём бота в список сразу: его тут же выбирают, и до ответа на
      // перезапрос список не должен считать его несуществующим.
      client.setQueryData<BotsResponse>(queryKeys.bots, (old) =>
        old
          ? { ...old, items: [...old.items, bot], names: { ...old.names, [String(bot.id)]: bot.name } }
          : old,
      )
      void client.invalidateQueries({ queryKey: queryKeys.bots })
    },
  })
}

export function useRenameBot() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => botsApi.rename(id, name),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.bots })
      toast.success('Бот переименован')
    },
  })
}

export function useDeleteBot() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => botsApi.remove(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.bots })
      toast.success('Бот удалён')
    },
    onError: onError('Не удалось удалить бота'),
  })
}

export function usePromoteBot() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => botsApi.promote(id),
    onSuccess: async (result) => {
      // Основной поменялся целиком: его анкета, сценарий и фразы в кэше
      // устарели независимо от того, какой бот сейчас выбран.
      await dropBotScoped(client)
      await refetchBotScoped(client)
      void client.invalidateQueries({ queryKey: queryKeys.bots })
      toast.success(`Готово. Прежний основной сохранён как «${result.backup.name}»`)
    },
    onError: onError('Не удалось сделать бота основным'),
  })
}
